import CallMadeIcon from '@mui/icons-material/CallMade';
import CallReceived from '@mui/icons-material/CallReceived';
import MessageIcon from '@mui/icons-material/Message';
import PlayCircleOutlineOutlinedIcon from '@mui/icons-material/PlayCircleOutlineOutlined';
import { Tooltip } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import {
  GridRenderCellParams,
  GridSortModel,
  GridValueFormatterParams,
} from '@mui/x-data-grid';
import clsx from 'clsx';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import cookie from 'react-cookies';
import { useDispatch } from 'react-redux';
import { NavLink, useLocation } from 'react-router-dom';
import { useRecoilValueLoadable } from 'recoil';

import { normalizePhoneSearch } from '../../../common/helperFunctions/phoneSearchNormalizer';
import { useBusinessPermissions } from '../../../common/helperFunctions/useBusinessPermissions';
import { isPayPerAgent } from '../../../common/isPayPerAgent';
import CrmIcon from '../../../images/crm-icon.svg';
import { endingDate } from '../../../recoil/atoms/endingDate';
import { startingDate } from '../../../recoil/atoms/startingDate';
import {
  IBusinessModel,
  IUserAtom,
  Role,
  user,
} from '../../../recoil/atoms/user';
import { methodNameEnum } from '../../../recoil/selectors/makePHPRequest';
import {
  IPermissions,
  permissionsSelector,
} from '../../../recoil/selectors/permissionsSelector';
import {
  endpointEnum,
  makeApiRequestCached,
} from '../../../services/rest/makeAPIRequest';
import HistoryExportModal from '../../components/modals/HistoryExportModal';
import { useSnackbarWithStyles } from '../../components/notifications/SnackbarProviderWithStyles';
import { updateDisplayLargeTableMsg } from '../../store/largeTableSlice';
import {
  DataTable,
  IColDef,
  IToolbarComponent,
} from '../Manage/pages/queues-table/DataTable';
import { EnhancedTableToolbar } from '../Manage/pages/queues-table/EnhancedTableToolbar';

const useStyles = makeStyles((these) => ({
  fullWidth: {
    width: '100% !important',
  },
  wordWrap: {
    'white-space': 'normal !important',
  },
  callMadeIcon: {
    color: '#26c281',
  },
  playIcon: {
    color: '#e87e04',
  },
  callIconBlue: {
    color: '#0098C7',
  },
  callIconGreen: {
    color: 'green',
  },
  messageIcon: {
    color: '#0098C7',
  },
  CrmIcon: {
    color: '#337ab7',
  },
  embedContainer: {
    '& img': {
      maxWidth: '100%',
    },
  },
}));

const renderCell = (params: GridRenderCellParams) => (
  <div dangerouslySetInnerHTML={{ __html: params.value as string }}></div>
);

export const SmsHistory = ({ agentId }: { agentId: any }) => {
  const classes = useStyles();
  const location = useLocation();
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);
  const [sortModel, setGridSortModel] = useState<GridSortModel>([
    {
      field: 'date',
      sort: 'desc',
    },
  ]);
  const [showExportModal, setShowExportModal] = useState<boolean>(false);
  const [csvProps, setCsvProps] = useState<any>({});
  const [search, setSearch] = useState<string>('');
  const userAtom = useRecoilValueLoadable(user);
  const { tier } = userAtom.contents as IUserAtom;
  const permissionsLoadable = useRecoilValueLoadable(permissionsSelector);
  const {
    role,
    canDownloadHistory,
  } = permissionsLoadable.contents as IPermissions;
  const isSuperAdminBoolean = role === Role.SUPERADMIN;
  const isAdminOrManager =
    isSuperAdminBoolean || role === Role.MANAGER || role === Role.ADMIN;

  const canDownloadHistoryBoolean = useMemo(
    () => isAdminOrManager && canDownloadHistory,
    [isAdminOrManager, canDownloadHistory]
  );
  const dispatch = useDispatch();
  const [businessDataCached, businessDataLoadable] = makeApiRequestCached<
    IBusinessModel | undefined
  >(
    {
      endpoint: endpointEnum.GET_BUSINESS_DATA,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_BUSINESS_DATA,
      }),
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.result?.[0] ?? cached.current
  );
  const startdate = useRecoilValueLoadable(startingDate);
  const enddate = useRecoilValueLoadable(endingDate);
  const accessToken = cookie.load('accessToken');

  const formattedStart = useMemo(() => {
    return (
      businessDataLoadable.state === 'hasValue' &&
      businessDataCached?.current &&
      moment
        .tz(startdate.contents, businessDataCached.current.timezone_php)
        .format()
    );
  }, [businessDataCached?.current, startdate?.contents]);
  const formattedEnd = useMemo(() => {
    return (
      businessDataLoadable.state === 'hasValue' &&
      businessDataCached?.current &&
      moment
        .tz(enddate.contents, businessDataCached.current.timezone_php)
        .endOf('day')
        .format()
    );
  }, [businessDataCached?.current, enddate?.contents]);

  const [callHistoryCached, callHistoryLoadable] = makeApiRequestCached<any>(
    {
      waitForPayload:
        businessDataLoadable.state === 'loading' ||
        !formattedStart ||
        !formattedEnd, // wait until state is not loading to run ajax call
      endpoint: 'getusermessagesadin',
      contentType: 'application/json;charset=UTF-8',
      authorization: accessToken,
      payloadJsonString: JSON.stringify({
        call: 'getBusinessCallHistory',
        start: (page - 1) * pageSize,
        length: pageSize,
        startdate: formattedStart ?? startdate?.contents,
        enddate: formattedEnd ?? enddate?.contents,
        react: 1,
        draw: 1,
        order: [
          {
            column: sortModel?.[0].field,
            dir: sortModel?.[0].sort,
          },
        ],
        search: {
          regex: false,
          value: normalizePhoneSearch(search)?.[0] || search,
        },
        timezone: businessDataCached.current?.timezone_php,
        v: agentId ? 'user' : 'business', // type
        ...(agentId ? { userid: agentId } : {}),
      }),
    },
    (loadable, cached) =>
      loadable.contents?.response?.data
        ? {
            ...loadable.contents?.response?.data,
            result: loadable.contents?.response?.data?.result?.map(
              (row: any) => ({
                ...row,
                id: row.messageid,
              })
            ),
          }
        : cached.current
  );

  /**
   * Reset page start on search
   *
   * @param value Search string
   */
  const handleSearchChange = useCallback(
    (value: string) => {
      // reset page back to 1
      setPage(1);
      setSearch(value);
    },
    [setSearch]
  );
  const ToolbarComponent = useCallback(
    (props: IToolbarComponent) => (
      <EnhancedTableToolbar
        {...props}
        search={search}
        setSearch={handleSearchChange}
        debounceTimeout={500} // add 1/2 second debounce vs 100ms default value
        isLoading={callHistoryLoadable.state === 'loading'} // do not block on search
        dataType="SMS"
        csvExport={canDownloadHistoryBoolean}
        onCsvExport={(dataType, start, end, csvData) => {
          const mmsCSVData = csvData.map((elem, index) => {
            const elemCopy = elem;
            const mediaurls = elemCopy.splice(7, 1);
            if (index !== 0) {
              elemCopy[7] = mediaurls?.[0]
                ? mediaurls[0] + ' ' + elemCopy[7]
                : elemCopy[7];
            }
            return elemCopy;
          });
          setCsvProps({
            dataType: dataType,
            start: start,
            end: end,
            csvData: mmsCSVData,
          });
          setShowExportModal(true);
        }}
        hideCsvExportButton={!canDownloadHistoryBoolean}
      />
    ),
    [search, callHistoryLoadable, canDownloadHistoryBoolean]
  );

  const handlePageChange = (page: number, details?: any) => {
    setPage(page);
  };

  const handlePageSizeChange = (pageSize: number, details?: any) => {
    setPageSize(pageSize);
  };

  const handleGridSortModelChange = (model: GridSortModel, details?: any) => {
    setGridSortModel(model);
  };

  const { enqueueSnackbarWithStyles } = useSnackbarWithStyles();
  const handleClickSendSMS = (event: any, url: string) => {
    if (isPayPerAgent()) {
      event.preventDefault();
      enqueueSnackbarWithStyles({
        message:
          'You do not have access to send sms messages. Please reach out to your account administrator to upgrade your access',
        alertProps: {
          severity: 'error',
        },
      });
    }
  };
  const readOnly = useBusinessPermissions().accountReadOnly;

  const capitalize = (params) => {
    return params.value.charAt(0).toUpperCase() + params.value.slice(1);
  };
  /**
   * Create column for DataGrid renderer
   * use useMemo to prevent unneeded rerenderings
   */
  const columns = useMemo<IColDef[]>(
    () => [
      {
        field: 'id',
        headerName: 'Event',
        renderCell: (params: GridRenderCellParams) => {
          return (
            <div>
              {params.row.direction === 'incoming' ? (
                <Tooltip title="Incoming">
                  <CallReceived className={classes.callIconBlue} />
                </Tooltip>
              ) : (
                <Tooltip title="Outgoing">
                  <CallMadeIcon className={classes.callIconGreen} />
                </Tooltip>
              )}
            </div>
          );
        },
      },
      {
        field: 'date',
        headerName: 'Date',
        valueFormatter: (params) =>
          moment(params.row.date || undefined)?.format('MM/DD/YYYY, hh:mm A'),
        width: 150,
      },
      {
        field: 'externalId',
        headerName: 'External ID',
        headerClassName: 'superStyle',
        width: 150,
        hide: !isSuperAdminBoolean,
        disableExport: !isSuperAdminBoolean,
        renderCell: (params: GridRenderCellParams) => {
          return (
            <Tooltip title="Plivo SMS ID">
              <div>
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href={`https://console.plivo.com/sms/logs/messages/${params.row.externalId}/`}
                >
                  {params.row.externalId}
                </a>
              </div>
            </Tooltip>
          );
        },
      },
      {
        field: 'direction',
        headerName: 'Type',
        renderCell: capitalize,
        valueFormatter: capitalize,
      },
      // {
      //   field: 'crmName',
      //   headerName: 'Name',
      //   width: 200,
      //   enableSorting: false,
      // },
      {
        field: 'internalName',
        headerName: 'Internal SMS ID',
        renderCell,
        width: 150,
      },
      {
        field: 'phonenumber',
        headerName: 'External Contact',
        width: 150,
        renderCell: (params: GridRenderCellParams) => {
          return (
            <div>
              {params.row.crmName} <br />
              {params.row.phonenumber}
            </div>
          );
        },
        valueFormatter: (params: GridValueFormatterParams) => {
          return `${params.row.crmName} ${params.row.phonenumber}`;
        },
      },
      {
        field: 'messagestate',
        headerName: 'Status',
        enableSorting: false,
      },
      {
        hide: true,
        field: 'mediaurls',
        headerName: 'MMS',
        headerClassName: clsx(classes.fullWidth, classes.wordWrap),
        cellClassName: clsx(classes.fullWidth, classes.wordWrap),
        enableSorting: false,
      },
      {
        width: 180,
        field: 'message',
        headerName: 'Message',
        headerClassName: clsx(classes.fullWidth, classes.wordWrap),
        cellClassName: clsx(classes.fullWidth, classes.wordWrap),
        enableSorting: false,
        renderCell: (params: GridRenderCellParams) => {
          const mediaurlsArr = params.row.mediaurls
            ? params.row.mediaurls.split(',')
            : [];
          return (
            <div
              style={{
                margin:
                  !!params.row.message?.length && !!mediaurlsArr?.length
                    ? '8px 0px'
                    : '0px',
              }}
            >
              {!!mediaurlsArr?.length &&
                mediaurlsArr.map((mediaurl: string, index: number) => {
                  const regexResult = /[^\\/]+\.[^\\/]+$/.exec(mediaurl);
                  const filename: string = regexResult
                    ? regexResult[0]
                    : 'Attachment';
                  return (
                    <div key={index} style={{ margin: '2px 0px' }}>
                      <a href={mediaurl} target="_blank" rel="noreferrer">
                        {filename}
                      </a>
                    </div>
                  );
                })}
              {!params.row.message?.length && mediaurlsArr?.length && (
                <div style={{ margin: '2px 0px' }}>[ ATTACHMENT ONLY ]</div>
              )}
              {!!params.row.message?.length && !!mediaurlsArr?.length && (
                <div style={{ margin: '4px 0px' }} />
              )}
              {!!params.row.message?.length && (
                <div style={{ margin: '2px 0px' }}>{params.row.message}</div>
              )}
            </div>
          );
        },
      },
      {
        field: 'actions',
        headerName: 'Actions',
        enableSorting: false,
        hide: readOnly,
        width: 150,
        headerAlign: 'center',
        align: 'center',
        disableExport: true,
        renderCell: (params: GridRenderCellParams) => {
          return (
            <div>
              {params.row.crmlink && (
                <span>
                  <a
                    href={params.row.crmlink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Tooltip title="Go to CRM">
                      <img
                        src={CrmIcon}
                        style={{ display: 'inline-block', width: '28px' }}
                        alt="crm icon"
                      />
                    </Tooltip>
                  </a>
                </span>
              )}
              {params.row.recordingurl && (
                <>
                  <CallMadeIcon className={classes.callMadeIcon} />
                  <a
                    href={params.row.recordingurl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <PlayCircleOutlineOutlinedIcon
                      className={classes.playIcon}
                    />
                  </a>
                </>
              )}
              <NavLink
                className="inline-sms pointer"
                data-userid={params.row.userid}
                data-businessid={params.row.businessid}
                data-number={params.row.phonenumber}
                onClick={(event) => {
                  handleClickSendSMS(
                    event,
                    `${location.pathname}/threaded-sms/${params.row.userid}/${params.row.phonenumber}`
                  );
                }}
                to={`${location.pathname}/threaded-sms/${params.row.userid}/${params.row.phonenumber}`}
              >
                {tier !== 'Basic' && (
                  <Tooltip title="Send SMS">
                    <MessageIcon className={classes.messageIcon} />
                  </Tooltip>
                )}
              </NavLink>
            </div>
          );
        },
      },
    ],
    [classes]
  );
  // alert user on BasePage.tsx that table is capped at 10k rows
  useEffect(() => {
    if (callHistoryCached.current?.total > 10_000) {
      dispatch(updateDisplayLargeTableMsg(true));
    }
  }, [callHistoryCached.current]);

  return (
    <>
      <HistoryExportModal
        show={showExportModal}
        handleClose={() => {
          setShowExportModal(false);
        }}
        page={page}
        pageSize={pageSize}
        totalRows={callHistoryCached.current?.total ?? 0}
        type="sms-history"
        csvProps={csvProps}
        agentId={agentId}
        rows={callHistoryCached?.current?.result}
        filters={{ search }}
        canDownloadHistoryBoolean={canDownloadHistoryBoolean}
      />
      <DataTable
        rows={callHistoryCached.current?.result ?? []}
        columns={columns}
        rowCount={
          (callHistoryCached.current?.total > 10_000
            ? 10_000
            : callHistoryCached.current?.total) ?? 0
        }
        displayCount={callHistoryCached.current?.total}
        page={page}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        paginationMode="server"
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={handleGridSortModelChange}
        loading={callHistoryLoadable.state === 'loading'}
        ToolbarComponent={ToolbarComponent}
        pagination
      />
    </>
  );
};
